import React, { useEffect, useState, useRef } from "react";
import "./verificationcode.scss";
import { toast } from "react-toastify";
import { Loader } from "react-bootstrap-typeahead";
import axios from "axios";

interface IPROPS {
  mobileNumber: any;
  setOpenOtpModal: any;
  resendOtp: any;
  onOtpVerified?: () => void; 
}

const MobileVerifyOtp: React.FC<IPROPS> = ({
  mobileNumber,
  setOpenOtpModal,
  resendOtp,
  onOtpVerified,
}) => {
  const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
  const [loader, setLoader] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  const fetchingTenant = window.location.hostname.split(".")[0];
  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const token = localStorage.getItem("customer-token");

  // Auto-focus first input on mount
  useEffect(() => {
    if (firstInputRef.current) {
      firstInputRef.current.focus();
    }
  }, []);

  const verifyOtp = async () => {
    const otp = otpValues.join("");

    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    const updatedPhone = {
      new_phone: mobileNumber,
      otp: otp,
    };

    setLoader(true);

    try {
      const response = await axios.post(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/gc/change-number/verify-otp/`,
        updatedPhone,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.data) {
        toast.success("OTP verified successfully. Phone number updated!");
        setOpenOtpModal(false);

        // **TRIGGER PROFILE REFETCH IN PARENT COMPONENT**
        if (onOtpVerified) {
          onOtpVerified();
        }
      }

      console.log("OTP verified successfully:", response.data);
    } catch (error: any) {
      console.error("Error verifying OTP:", error);

      // Show specific error messages from API
      if (error.response?.data) {
        const errorData = error.response.data;
        if (typeof errorData === "string") {
          toast.error(errorData);
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (errorData.otp) {
          toast.error(
            Array.isArray(errorData.otp) ? errorData.otp[0] : errorData.otp
          );
        } else {
          toast.error("Invalid OTP. Please try again.");
        }
      } else {
        toast.error("An error occurred while verifying OTP");
      }
    } finally {
      setLoader(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    // Only allow numeric input
    if (value && !/^\d$/.test(value)) return;

    if (value.length > 1) return; // Prevent more than 1 char

    const updatedOtp = [...otpValues];
    updatedOtp[index] = value;
    setOtpValues(updatedOtp);

    // Move focus to the next input automatically
    if (value && index < 5) {
      const nextInput = document.getElementById(
        `otp-${index + 1}`
      ) as HTMLInputElement;
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    // Handle backspace to move to previous input
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      const prevInput = document.getElementById(
        `otp-${index - 1}`
      ) as HTMLInputElement;
      if (prevInput) {
        prevInput.focus();
      }
    }

    // Submit on Enter key if OTP is complete
    if (e.key === "Enter" && otpValues.join("").length === 6) {
      verifyOtp();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Only allow 6-digit numbers
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      setOtpValues(digits);

      // Focus the last input
      const lastInput = document.getElementById(`otp-5`) as HTMLInputElement;
      if (lastInput) lastInput.focus();
    } else {
      toast.error("Please paste a valid 6-digit OTP");
    }
  };

  const resendOtpHandler = () => {
    setOtpValues(["", "", "", "", "", ""]); // Clear OTP inputs
    resendOtp();
    // Focus first input after resend
    setTimeout(() => {
      if (firstInputRef.current) {
        firstInputRef.current.focus();
      }
    }, 100);
  };

  return (
    <div className="verification-container" style={{ padding: "0" }}>
      <h2>Verification Code</h2>
      <p className="instruction">
        Enter the verification code sent to <strong>{mobileNumber}</strong>
      </p>

      <div className="code-inputs" style={{ gap: "5px" }}>
        {otpValues.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            ref={index === 0 ? firstInputRef : null}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : undefined}
            className="otp-input"
          />
        ))}
      </div>

      <button
        className="confirm-btn"
        onClick={verifyOtp}
        disabled={loader || otpValues.join("").length !== 6}
      >
        {loader ? (
          <>
            Confirming...
            <Loader />
          </>
        ) : (
          "Confirm"
        )}
      </button>

      <button
        className="resend-code"
        onClick={resendOtpHandler}
        disabled={loader}
      >
        Resend Code
      </button>
    </div>
  );
};

export default MobileVerifyOtp;
